import React, { useContext } from 'react';
import cx from 'classnames';
import { Tag, Link } from '@ontruck/orc';
import { NavLink } from 'react-router-dom';

import GatekeepingUIContext from 'modules/gatekeeping/ui-context';
import { StopCell } from 'modules/fleet/list';
import ServiceTag, { SKINS } from 'components/ServiceTag';
import { injectIntl } from 'react-intl';
import {
  PalletsCell,
  WeightCell,
  HeightCell,
  ExtrasCell,
  DistanceCell,
  RouteType,
  CommentsCell,
} from 'utils/format';
import { IS_TENANT } from 'tenant/config';
import { DISTRIBUTION, COLLECTION } from 'redux/modules/shipper-orders/utils';
import { ShippersTooltip } from 'modules/fleet/common';

const cellWidth = (width) => ({ style: { minWidth: width, maxWidth: width } });
const isExtraPresent = (extra, extras = []) => extras.indexOf(extra) !== -1;

const Ghost = (props) => <div {...props} />;

const GateKeepingListItem = ({ className, showActionsButton, loading, to, shipperOrder, intl }) => {
  const { isLoading } = useContext(GatekeepingUIContext);
  const t = intl.messages.modules.Gatekeeping.list_item;
  const isMultiroute = shipperOrder?.is_multiroute;
  const isRecurring = shipperOrder?.is_recurring;

  const start_label = shipperOrder?.start_label;
  const end_label = shipperOrder?.end_label;

  const hasComments = shipperOrder?.has_comments;

  const BaseComponent = shipperOrder && to ? NavLink : Ghost;

  const isBeenEdited = isLoading(shipperOrder?.id);

  const isCore = shipperOrder?.is_core;
  const isLHExpress = shipperOrder?.is_lh_express;
  const hasHandling = !!shipperOrder?.requires_handling;

  const shipperOrdersForTooltip = [shipperOrder];

  return (
    <BaseComponent
      to={to}
      className={cx(className, {
        [`${className}__loading`]: loading || isLoading(shipperOrder?.id),
      })}
    >
      <div
        className="cell cell__nowrap cell__dark cell__shipper"
        {...cellWidth('300px')}
        data-testid="gatekeeping-list-item"
      >
        {/* 
        for tenant, company name will open a tooltip
        this tooltip can be used to navigate to so detail
         */}
        {IS_TENANT && !!shipperOrder && (
          <ShippersTooltip shipperOrders={shipperOrdersForTooltip} placement="right">
            <span className="cell__shipper__name">{shipperOrder?.shipper?.company_name}</span>
          </ShippersTooltip>
        )}
        {!IS_TENANT && (
          <span className="cell__shipper__name">{shipperOrder?.shipper?.company_name}</span>
        )}
        <div className="cell__shipper__tags">
          {IS_TENANT ? (
            <>
              {shipperOrder?.journey_type === COLLECTION && (
                <ServiceTag text={t.export} skin={SKINS.grey} textTransform="uppercase" />
              )}
              {shipperOrder?.journey_type === DISTRIBUTION && (
                <ServiceTag text={t.import} skin={SKINS.deepPurple} textTransform="uppercase" />
              )}
            </>
          ) : (
            <>
              {!!shipperOrder?.shipper?.is_prio && (
                <ServiceTag text={t.prio} skin={SKINS.primary} textTransform="uppercase" />
              )}
              {!!shipperOrder?.shipper?.is_ftb && (
                <ServiceTag text={t.ftb} skin={SKINS.success} textTransform="uppercase" />
              )}
              {!!shipperOrder?.shipper?.is_recovering && (
                <ServiceTag text={t.recovering} skin={SKINS.accent} textTransform="uppercase" />
              )}
              {isCore === false && (
                <ServiceTag text={t.non_core} skin={SKINS.deepPurple} textTransform="uppercase" />
              )}
              {isLHExpress === true && (
                <ServiceTag text={t.lh_express} skin={SKINS.lhExpress} textTransform="uppercase" />
              )}
            </>
          )}
        </div>
      </div>
      <div className="cell" {...cellWidth('36px')}>
        <RouteType isMultiroute={isMultiroute} isRecurring={isRecurring} />
      </div>
      <div
        data-tip={shipperOrder?.id}
        data-for="stops-tooltip"
        data-event="click"
        className="tooltip-container"
      >
        <StopCell
          label={start_label}
          timeFrom={shipperOrder?.start_time_from}
          timeTo={shipperOrder?.start_time_to}
          {...cellWidth('136px')}
        />

        <div className="cell cell__center" {...cellWidth('64px')}>
          <Link
            text={
              <DistanceCell
                distance={shipperOrder?.distance}
                distanceMiles={shipperOrder?.distance_miles}
                country={shipperOrder?.route?.country}
              />
            }
          />
        </div>

        <StopCell
          label={end_label}
          timeFrom={shipperOrder?.end_time_from}
          timeTo={shipperOrder?.end_time_to}
          firstStopTimeFrom={shipperOrder?.start_time_from}
          {...cellWidth('136px')}
        />

        <div className="cell" {...cellWidth('36px')}>
          <CommentsCell hasComments={hasComments} />
        </div>
      </div>
      <div className="cell cell__bold cell__right cell__dark" {...cellWidth('96px')}>
        <PalletsCell
          realPallets={shipperOrder?.real_pallets}
          equivalentPallets={shipperOrder?.equivalent_pallets}
          typePallets={shipperOrder?.type_pallets}
          country={shipperOrder?.route?.country}
          stops={[shipperOrder]}
        />
      </div>
      <div className="cell cell__right cell__dark" {...cellWidth('72px')}>
        <WeightCell
          weight={IS_TENANT ? shipperOrder?.total_goods_weight : shipperOrder?.total_weight}
        />
      </div>
      <div className="cell cell__right cell__dark" {...cellWidth('56px')}>
        <HeightCell height={shipperOrder?.item_height} />
      </div>
      <div className="cell cell__flex cell__dark" {...cellWidth('114px')}>
        <ExtrasCell
          handling={hasHandling}
          lifgate={isExtraPresent('LIFTGATE', shipperOrder?.extras)}
          sideload={isExtraPresent('SIDE_LOAD', shipperOrder?.extras)}
          topload={isExtraPresent('TOP_LOAD', shipperOrder?.extras)}
        />
      </div>
      <div className="cell cell__grow">
        {shipperOrder?.human_review_reasons?.map((reason) => (
          <Tag
            key={reason}
            data-tip={t.reasons_long[reason] || reason}
            data-for="reason-tooltip"
            text={t.reasons[reason] || reason}
            skin="modernLightGrey"
            weight="bold"
            size="M"
            textTransform="uppercase"
            className="reason"
          />
        ))}
      </div>
      <div className="cell" {...cellWidth('76px')}>
        <button
          data-tip={shipperOrder?.id}
          data-for="actions-tooltip"
          data-event="click"
          className={cx('actions_button', {
            actions_button__force_show: showActionsButton,
          })}
          style={isBeenEdited || loading ? { display: 'none' } : {}}
        >
          {t.actions}
        </button>
      </div>
    </BaseComponent>
  );
};

export default injectIntl(GateKeepingListItem);
